import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  createTenantIndexedDBStorage,
  reviveDates,
  type PersistedReplaySessionState,
} from './tenantStorage';
import type { WebGrpcDecodedData } from '@terra/shared-schemas-tracker';
import type { StoredHarDisplayEntry } from '@terra/shared-utils';
import { type ConnectionTarget, type ReplayCollection } from '../types/replay';
import {
  textIncludesPortalBinaryWireBody,
  type BodyEncoding,
} from '../electron-shared/utils/binary-body';
import {
  formatRequestToText,
  parseRequestFromText,
} from '../utils/httpFormatter';
import { insertItemBeforeId, moveReplaySession } from './replay-session-order';

function extractDisplayPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0] || url;
  }
}

function titleMatchesUrl(title: string, url: string): boolean {
  return title === extractDisplayPath(url);
}

const MAX_PERSISTED_BODY_SIZE = 1024 * 1024;
const MAX_PERSISTED_RESPONSES_PER_SESSION = 50;

export const DEFAULT_REPLAY_COLLECTION_ID = 'default-replay-collection';
const DEFAULT_REPLAY_COLLECTION_NAME = 'Default Collection';

function createDefaultReplayCollection(): ReplayCollection {
  const now = new Date();

  return {
    id: DEFAULT_REPLAY_COLLECTION_ID,
    name: DEFAULT_REPLAY_COLLECTION_NAME,
    description: 'Default collection for replay sessions',
    createdAt: now,
    updatedAt: now,
  };
}

function clampResponseIndexForHistory({
  currentResponseIndex,
  historyLength,
  droppedCount = 0,
}: {
  currentResponseIndex: number;
  historyLength: number;
  droppedCount?: number;
}): number {
  if (historyLength === 0) {
    return -1;
  }

  return Math.max(
    0,
    Math.min(currentResponseIndex - droppedCount, historyLength - 1),
  );
}

function isNameTaken(
  collections: ReplayCollection[],
  name: string,
  excludeCollectionId?: string,
): boolean {
  const normalized = name.trim().toLowerCase();

  if (!normalized) return false;

  return collections.some(
    (collection) =>
      collection.id !== excludeCollectionId &&
      collection.name.trim().toLowerCase() === normalized,
  );
}

export type ReplaySession = {
  id: string;
  title: string;
  collectionId: string;
  applicationId?: string;
  request: {
    method: string;
    url: string;
    headers: Record<string, string | string[]>;
    rawHeaders?: string[];
    body?: string;
    bodyEncoding?: BodyEncoding;
  };
  responseHistory: Array<{
    statusCode: number;
    statusMessage: string;
    headers: Record<string, string | string[]>;
    rawHeaders?: string[];
    body: string;
    duration: number;
    error?: string;
    httpVersion?: string;
    timestamp: Date;
    requestData: string; // Store the request that produced this response
    /** Credential used for this request (for "Run All" feature) */
    credential?: {
      id: string;
      username: string;
      role: string;
    };
    webGrpcDecodedData?: WebGrpcDecodedData | null;
    sentRequestWire?: string;
    sentRequestWireEncoding?: BodyEncoding;
  }>;
  currentResponseIndex: number;
  requestData: string; // Current edited request text
  grpcHarDisplay?: StoredHarDisplayEntry;
  protocol?: 'http1' | 'http2';
  preserveContentLength?: boolean;
  autoFollowRedirects?: boolean;
  syncTargetWithHostHeader?: boolean;
  maxRedirects?: number;
  connectionTarget: ConnectionTarget;
  activeCredential: {
    id: string;
    username: string;
    role: string;
  } | null;
};

interface ReplaySessionState {
  collections: ReplayCollection[];
  sessions: ReplaySession[];
  activeSessionId: string | null;
  activeCollectionId: string;
  collapsedCollectionIds: string[];
  isSessionsLineCollapsed: boolean;
  setSessionsLineCollapsed: (collapsed: boolean) => void;

  addSession: (
    session: Omit<ReplaySession, 'id' | 'collectionId'> & {
      collectionId?: string;
    },
  ) => string;
  loadRequestIntoSession: ({
    sessionId,
    session,
  }: {
    sessionId: string;
    session: Omit<ReplaySession, 'id' | 'collectionId' | 'activeCredential'> & {
      collectionId?: string;
      activeCredential?: ReplaySession['activeCredential'];
    };
  }) => void;
  removeSession: (sessionId: string) => void;
  setActiveSession: (sessionId: string | null) => void;
  setActiveCollection: (collectionId: string) => void;
  createCollection: (name: string) => string | null;
  renameCollection: (collectionId: string, name: string) => boolean;
  deleteCollection: (collectionId: string) => void;
  isCollectionNameTaken: (
    name: string,
    excludeCollectionId?: string,
  ) => boolean;
  moveSessionToCollection: (
    sessionId: string,
    collectionId: string,
    beforeSessionId?: string,
  ) => void;
  reorderCollections: (
    collectionId: string,
    beforeCollectionId: string,
  ) => void;
  toggleCollectionCollapsed: (collectionId: string) => void;
  collapseAllCollections: () => void;
  expandAllCollections: () => void;
  updateSessionRequest: (sessionId: string, requestData: string) => void;
  updateSessionGrpcReplayTransform: ({
    sessionId,
    requestData,
    wireBody,
    wireBodyEncoding,
    grpcHarDisplay,
  }: {
    sessionId: string;
    requestData: string;
    wireBody: string;
    wireBodyEncoding?: BodyEncoding;
    grpcHarDisplay: StoredHarDisplayEntry;
  }) => void;
  updateResponseWebGrpcDecodedData: ({
    sessionId,
    responseIndex,
    webGrpcDecodedData,
  }: {
    sessionId: string;
    responseIndex: number;
    webGrpcDecodedData: WebGrpcDecodedData | null;
  }) => void;
  updateSessionTitle: (sessionId: string, title: string) => void;
  updateSessionProtocol: (
    sessionId: string,
    protocol: 'http1' | 'http2',
  ) => void;
  updateSessionPreserveContentLength: (
    sessionId: string,
    preserveContentLength: boolean,
  ) => void;
  updateSessionAutoFollowRedirects: (
    sessionId: string,
    autoFollowRedirects: boolean,
  ) => void;
  updateSessionSyncTargetWithHostHeader: (
    sessionId: string,
    syncTargetWithHostHeader: boolean,
  ) => void;
  updateSessionConnectionTarget: (
    sessionId: string,
    target: Partial<ConnectionTarget>,
  ) => void;
  updateSessionActiveCredential: (
    sessionId: string,
    credential: ReplaySession['activeCredential'],
  ) => void;
  addResponseToSession: (
    sessionId: string,
    response: ReplaySession['responseHistory'][0],
  ) => void;
  setCurrentResponseIndex: (sessionId: string, index: number) => void;
  clearSessionHistory: (sessionId: string) => void;
  clearAllSessions: () => void;
}

export const useReplaySessionStore = create<ReplaySessionState>()(
  persist(
    (set, get) => ({
      collections: [createDefaultReplayCollection()],
      sessions: [],
      activeSessionId: null,
      activeCollectionId: DEFAULT_REPLAY_COLLECTION_ID,
      collapsedCollectionIds: [],
      isSessionsLineCollapsed: false,

      setSessionsLineCollapsed: (collapsed) => {
        set({ isSessionsLineCollapsed: collapsed });
      },

      addSession: (session) => {
        const id = `replay-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        set((state) => ({
          collections:
            state.collections.length > 0
              ? state.collections
              : [createDefaultReplayCollection()],
          sessions: [
            ...state.sessions,
            {
              ...session,
              id,
              collectionId:
                session.collectionId ??
                state.activeCollectionId ??
                DEFAULT_REPLAY_COLLECTION_ID,
              activeCredential: session.activeCredential ?? null,
            },
          ],
          activeSessionId: id,
          activeCollectionId:
            session.collectionId ??
            state.activeCollectionId ??
            DEFAULT_REPLAY_COLLECTION_ID,
        }));

        return id;
      },

      loadRequestIntoSession: ({ sessionId, session }) => {
        set((state) => {
          const existingSession = state.sessions.find(
            (item) => item.id === sessionId,
          );

          if (!existingSession) {
            return state;
          }

          const collectionId =
            session.collectionId ??
            existingSession.collectionId ??
            state.activeCollectionId ??
            DEFAULT_REPLAY_COLLECTION_ID;

          return {
            sessions: state.sessions.map((item) =>
              item.id === sessionId
                ? {
                    ...item,
                    title: session.title,
                    applicationId: session.applicationId,
                    request: session.request,
                    requestData: session.requestData,
                    protocol: session.protocol,
                    connectionTarget: session.connectionTarget,
                    grpcHarDisplay: session.grpcHarDisplay,
                    responseHistory: [],
                    currentResponseIndex: -1,
                    activeCredential: session.activeCredential ?? null,
                    collectionId,
                  }
                : item,
            ),
            activeSessionId: sessionId,
            activeCollectionId: collectionId,
          };
        });
      },

      removeSession: (sessionId) => {
        set((state) => {
          const newSessions = state.sessions.filter((s) => s.id !== sessionId);
          const newActiveId =
            state.activeSessionId === sessionId
              ? newSessions[0]?.id || null
              : state.activeSessionId;

          return {
            sessions: newSessions,
            activeSessionId: newActiveId,
          };
        });
      },

      setActiveSession: (sessionId) => {
        set({ activeSessionId: sessionId });
      },

      setActiveCollection: (collectionId) => {
        set((state) => {
          if (
            !state.collections.some(
              (collection) => collection.id === collectionId,
            )
          ) {
            return state;
          }

          const firstSessionInCollection = state.sessions.find(
            (session) => session.collectionId === collectionId,
          );

          return {
            activeCollectionId: collectionId,
            activeSessionId:
              firstSessionInCollection?.id ?? state.activeSessionId,
          };
        });
      },

      createCollection: (name) => {
        const trimmedName = name.trim();

        if (!trimmedName) {
          return null;
        }

        if (isNameTaken(get().collections, trimmedName)) {
          return null;
        }

        const id = `replay-collection-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const now = new Date();

        set((current) => ({
          collections: [
            ...current.collections,
            {
              id,
              name: trimmedName,
              createdAt: now,
              updatedAt: now,
            },
          ],
          activeCollectionId: id,
        }));

        return id;
      },

      renameCollection: (collectionId, name) => {
        const trimmedName = name.trim();

        if (!trimmedName) {
          return false;
        }

        if (isNameTaken(get().collections, trimmedName, collectionId)) {
          return false;
        }

        set((current) => ({
          collections: current.collections.map((collection) =>
            collection.id === collectionId
              ? { ...collection, name: trimmedName, updatedAt: new Date() }
              : collection,
          ),
        }));

        return true;
      },

      isCollectionNameTaken: (name, excludeCollectionId) =>
        isNameTaken(get().collections, name, excludeCollectionId),

      deleteCollection: (collectionId) => {
        if (collectionId === DEFAULT_REPLAY_COLLECTION_ID) {
          return;
        }

        set((state) => {
          const defaultCollection = state.collections.find(
            (collection) => collection.id === DEFAULT_REPLAY_COLLECTION_ID,
          );
          const nextCollections = state.collections.filter(
            (collection) => collection.id !== collectionId,
          );
          const collections =
            defaultCollection || nextCollections.length > 0
              ? nextCollections
              : [createDefaultReplayCollection()];
          const sessions = state.sessions.map((session) =>
            session.collectionId === collectionId
              ? { ...session, collectionId: DEFAULT_REPLAY_COLLECTION_ID }
              : session,
          );
          const activeSession =
            state.activeSessionId &&
            sessions.find((session) => session.id === state.activeSessionId);
          const nextActiveCollectionId = activeSession
            ? activeSession.collectionId
            : state.activeCollectionId === collectionId
              ? DEFAULT_REPLAY_COLLECTION_ID
              : state.activeCollectionId;

          return {
            collections,
            sessions,
            activeCollectionId: nextActiveCollectionId,
            collapsedCollectionIds: state.collapsedCollectionIds.filter(
              (id) => id !== collectionId,
            ),
          };
        });
      },

      moveSessionToCollection: (sessionId, collectionId, beforeSessionId) => {
        set((state) => {
          if (
            !state.collections.some(
              (collection) => collection.id === collectionId,
            )
          ) {
            return state;
          }

          return {
            sessions: moveReplaySession({
              sessions: state.sessions,
              sessionId,
              collectionId,
              beforeSessionId,
            }),
            activeCollectionId:
              state.activeSessionId === sessionId
                ? collectionId
                : state.activeCollectionId,
          };
        });
      },

      reorderCollections: (collectionId, beforeCollectionId) => {
        set((state) => {
          if (collectionId === beforeCollectionId) {
            return state;
          }

          if (
            !state.collections.some(
              (collection) => collection.id === collectionId,
            ) ||
            !state.collections.some(
              (collection) => collection.id === beforeCollectionId,
            )
          ) {
            return state;
          }

          return {
            collections: insertItemBeforeId({
              items: state.collections,
              itemId: collectionId,
              beforeId: beforeCollectionId,
            }),
          };
        });
      },

      toggleCollectionCollapsed: (collectionId) => {
        set((state) => ({
          collapsedCollectionIds: state.collapsedCollectionIds.includes(
            collectionId,
          )
            ? state.collapsedCollectionIds.filter((id) => id !== collectionId)
            : [...state.collapsedCollectionIds, collectionId],
        }));
      },

      collapseAllCollections: () => {
        set((state) => ({
          collapsedCollectionIds: state.collections.map(
            (collection) => collection.id,
          ),
        }));
      },

      expandAllCollections: () => {
        set({ collapsedCollectionIds: [] });
      },

      updateSessionGrpcReplayTransform: ({
        sessionId,
        requestData,
        wireBody,
        wireBodyEncoding,
        grpcHarDisplay,
      }) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  requestData,
                  grpcHarDisplay,
                  request: {
                    ...session.request,
                    body: wireBody,
                    bodyEncoding: wireBodyEncoding,
                  },
                }
              : session,
          ),
        }));
      },

      updateResponseWebGrpcDecodedData: ({
        sessionId,
        responseIndex,
        webGrpcDecodedData,
      }) => {
        set((state) => ({
          sessions: state.sessions.map((session) => {
            if (session.id !== sessionId) {
              return session;
            }

            if (
              responseIndex < 0 ||
              responseIndex >= session.responseHistory.length
            ) {
              return session;
            }

            return {
              ...session,
              responseHistory: session.responseHistory.map((response, index) =>
                index === responseIndex
                  ? { ...response, webGrpcDecodedData }
                  : response,
              ),
            };
          }),
        }));
      },

      updateSessionRequest: (sessionId, requestData) => {
        set((state) => ({
          sessions: state.sessions.map((session) => {
            if (session.id !== sessionId) return session;

            const parsed = parseRequestFromText({
              text: requestData,
              originalUrl: session.request.url,
            });

            if (!parsed) return { ...session, requestData };

            const methodChanged = parsed.method !== session.request.method;
            const urlChanged = parsed.url !== session.request.url;

            if (!methodChanged && !urlChanged) {
              return { ...session, requestData };
            }

            const hasAutoTitle = titleMatchesUrl(
              session.title,
              session.request.url,
            );

            return {
              ...session,
              requestData,
              request: {
                ...session.request,
                method: parsed.method,
                url: parsed.url,
              },
              ...(hasAutoTitle && urlChanged
                ? { title: extractDisplayPath(parsed.url) }
                : {}),
            };
          }),
        }));
      },

      updateSessionTitle: (sessionId, title) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId ? { ...session, title } : session,
          ),
        }));
      },

      updateSessionProtocol: (sessionId, protocol) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId ? { ...session, protocol } : session,
          ),
        }));
      },

      updateSessionPreserveContentLength: (
        sessionId,
        preserveContentLength,
      ) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? { ...session, preserveContentLength }
              : session,
          ),
        }));
      },

      updateSessionAutoFollowRedirects: (sessionId, autoFollowRedirects) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? { ...session, autoFollowRedirects }
              : session,
          ),
        }));
      },

      updateSessionSyncTargetWithHostHeader: (
        sessionId,
        syncTargetWithHostHeader,
      ) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? { ...session, syncTargetWithHostHeader }
              : session,
          ),
        }));
      },

      updateSessionConnectionTarget: (sessionId, target) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  connectionTarget: { ...session.connectionTarget, ...target },
                }
              : session,
          ),
        }));
      },

      updateSessionActiveCredential: (sessionId, credential) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? { ...session, activeCredential: credential }
              : session,
          ),
        }));
      },

      addResponseToSession: (sessionId, response) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  responseHistory: [...session.responseHistory, response],
                  currentResponseIndex: session.responseHistory.length,
                }
              : session,
          ),
        }));
      },

      setCurrentResponseIndex: (sessionId, index) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? { ...session, currentResponseIndex: index }
              : session,
          ),
        }));
      },

      clearSessionHistory: (sessionId) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  responseHistory: [],
                  currentResponseIndex: -1,
                }
              : session,
          ),
        }));
      },

      clearAllSessions: () => {
        set({
          collections: [createDefaultReplayCollection()],
          sessions: [],
          activeSessionId: null,
          activeCollectionId: DEFAULT_REPLAY_COLLECTION_ID,
          collapsedCollectionIds: [],
        });
      },
    }),
    {
      name: 'terra-proxy-replay-sessions',
      storage: createJSONStorage(() => createTenantIndexedDBStorage()),
      partialize: (state) => ({
        collections: state.collections,
        sessions: state.sessions.map((session) => {
          const truncateTextBody = (body: string | undefined) =>
            body && body.length > MAX_PERSISTED_BODY_SIZE
              ? body.slice(0, MAX_PERSISTED_BODY_SIZE) +
                '\n\n[Truncated for storage]'
              : body;

          const isBinaryRequest = session.request.bodyEncoding === 'base64';
          const requestBodyTooLarge =
            !!session.request.body &&
            session.request.body.length > MAX_PERSISTED_BODY_SIZE;

          const truncatedHistory = session.responseHistory
            .slice(-MAX_PERSISTED_RESPONSES_PER_SESSION)
            .map((resp) => ({
              ...resp,
              body:
                resp.body.length > MAX_PERSISTED_BODY_SIZE
                  ? resp.body.slice(0, MAX_PERSISTED_BODY_SIZE) +
                    '\n\n[Truncated for storage]'
                  : resp.body,
            }));

          const droppedCount =
            session.responseHistory.length - truncatedHistory.length;
          const clampedIndex = clampResponseIndexForHistory({
            currentResponseIndex: session.currentResponseIndex,
            historyLength: truncatedHistory.length,
            droppedCount,
          });

          return {
            ...session,
            currentResponseIndex: clampedIndex,
            request: {
              ...session.request,
              body: isBinaryRequest
                ? requestBodyTooLarge
                  ? '[Binary body too large for storage]'
                  : session.request.body
                : truncateTextBody(session.request.body),
              bodyEncoding:
                isBinaryRequest && requestBodyTooLarge
                  ? undefined
                  : session.request.bodyEncoding,
            },
            requestData:
              session.requestData.length > MAX_PERSISTED_BODY_SIZE
                ? undefined
                : session.requestData,
            responseHistory: truncatedHistory,
          };
        }),
        activeSessionId: state.activeSessionId,
        activeCollectionId: state.activeCollectionId,
        collapsedCollectionIds: state.collapsedCollectionIds,
      }),
      merge: (persistedState, currentState) => {
        const revivedState = reviveDates(
          persistedState as PersistedReplaySessionState,
        );
        const persistedCollections = revivedState.collections;
        const defaultCollection = createDefaultReplayCollection();
        const mergedCollections =
          persistedCollections && persistedCollections.length > 0
            ? persistedCollections.some(
                (collection) => collection.id === DEFAULT_REPLAY_COLLECTION_ID,
              )
              ? persistedCollections
              : [defaultCollection, ...persistedCollections]
            : [defaultCollection];
        const mergedSessions = (
          revivedState.sessions ?? currentState.sessions
        ).map((session) => {
          const requestData = session.requestData;
          const shouldRegenerateRequestData =
            !requestData ||
            requestData.length === 0 ||
            textIncludesPortalBinaryWireBody(requestData);

          return {
            ...session,
            collectionId: session.collectionId ?? DEFAULT_REPLAY_COLLECTION_ID,
            activeCredential: session.activeCredential ?? null,
            currentResponseIndex: clampResponseIndexForHistory({
              currentResponseIndex: session.currentResponseIndex,
              historyLength: session.responseHistory.length,
            }),
            requestData: shouldRegenerateRequestData
              ? formatRequestToText(session.request)
              : requestData,
          };
        });
        const activeSessionId =
          revivedState.activeSessionId ?? currentState.activeSessionId;
        const activeSession = activeSessionId
          ? mergedSessions.find((session) => session.id === activeSessionId)
          : null;
        const candidateCollectionId =
          activeSession?.collectionId ??
          revivedState.activeCollectionId ??
          currentState.activeCollectionId;
        const activeCollectionId = mergedCollections.some(
          (collection) => collection.id === candidateCollectionId,
        )
          ? candidateCollectionId
          : DEFAULT_REPLAY_COLLECTION_ID;
        const collapsedCollectionIds =
          revivedState.collapsedCollectionIds?.filter((collectionId) =>
            mergedCollections.some(
              (collection) => collection.id === collectionId,
            ),
          ) ?? currentState.collapsedCollectionIds;

        return {
          ...currentState,
          ...revivedState,
          collections: mergedCollections,
          sessions: mergedSessions,
          activeCollectionId,
          collapsedCollectionIds,
        };
      },
    },
  ),
);
